'use client';

import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import posthog from 'posthog-js';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Heart,
  MessageCircle,
  Play,
  Share2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ClipComments, { SignInPrompt } from '@/components/clips/ClipComments';
import { ShareSheet } from '@/components/ShareSheet';
import { useClipEngagement } from '@/components/clips/useClipEngagement';
import { useAuth } from '@/contexts/AuthContext';
import { clipCaption } from '@/lib/clip-caption';
import { VIEW_RECORD_AFTER_SEC, formatCount } from '@/lib/clip-engagement';
import { recordClipView } from '@/lib/clip-engagement-api';
import { startClipView } from '@/lib/clip-view-tracker';
import { getDisplayImageUrl } from '@/lib/image-upload';
import type { Clip } from '@/lib/supabase';

export interface ClipCreator {
  gamertag: string;
  profileImageUrl?: string | null;
}

interface ClipViewerProps {
  clips: Clip[];
  /** Index of the open clip, or null when the viewer is closed. */
  index: number | null;
  creator: ClipCreator;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** Which page hosts the viewer (analytics). */
  source: 'profile' | 'app_profile' | 'feed';
  /** The viewer was opened from a shared `?clip=` link (analytics). */
  deepLinked?: boolean;
}

type NavMethod = 'button' | 'key' | 'wheel' | 'swipe';
type CloseMethod = 'esc' | 'x' | 'backdrop' | 'back';

/**
 * Full-screen, TikTok-style viewer for a profile's clips: one 9:16 clip at a
 * time, up/down (buttons, arrow keys, wheel, swipe) to move through the list.
 */
export default function ClipViewer({
  clips,
  index,
  creator,
  onIndexChange,
  onClose,
  source,
  deepLinked,
}: ClipViewerProps) {
  const open = index !== null && !!clips[index];
  return (
    <AnimatePresence>
      {open && (
        <ViewerDialog
          key="clip-viewer"
          clips={clips}
          index={index}
          creator={creator}
          onIndexChange={onIndexChange}
          onClose={onClose}
          source={source}
          deepLinked={deepLinked}
        />
      )}
    </AnimatePresence>
  );
}

const SWIPE_MIN_PX = 60;
const WHEEL_STEP = 50;
const WHEEL_LOCK_MS = 700;
const WHEEL_GESTURE_GAP_MS = 200;
const SLIDE_EASE = [0.32, 0.72, 0, 1] as const;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return desktop;
}

function ViewerDialog({
  clips,
  index,
  creator,
  onIndexChange,
  onClose: closeViewer,
  source,
  deepLinked,
}: ClipViewerProps & { index: number }) {
  const clip = clips[index];
  const isPresent = useIsPresent();
  const reduceMotion = useReducedMotion() ?? false;
  const [muted, setMuted] = useState(true);
  const [direction, setDirection] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [signInNudge, setSignInNudge] = useState(false);
  const isDesktop = useIsDesktop();
  const commentsButtonRef = useRef<HTMLButtonElement>(null);

  // Views / hearts / comments. Everything engagement-related stays hidden
  // while `available` is false (schema not deployed, or the read failed).
  const { user } = useAuth();
  const viewerId = user?.id ?? null;
  const clipIds = useMemo(() => clips.map((c) => c.id), [clips]);
  const engagement = useClipEngagement(clipIds, viewerId);
  const counts = engagement.countsFor(clip.id);
  const reacted = engagement.reacted.has(clip.id);

  const dialogRef = useRef<HTMLDivElement>(null);
  const togglePlayRef = useRef<(() => void) | null>(null);
  const wheelRef = useRef({ last: 0, lockUntil: 0, acc: 0 });
  const touchRef = useRef<{ x: number; y: number } | null>(null);
  const swipedRef = useRef(false);

  const onAutoplayBlocked = useCallback(() => setMuted(true), []);

  // Analytics: one opened/closed pair per viewer session.
  const sessionRef = useRef<{ openedAt: number; viewed: Set<string>; closed: boolean } | null>(null);
  const closeMethodRef = useRef<CloseMethod | null>(null);
  if (sessionRef.current === null) {
    sessionRef.current = { openedAt: Date.now(), viewed: new Set(), closed: false };
  }
  sessionRef.current.viewed.add(clip.id);

  const openedRef = useRef(false);
  useEffect(() => {
    // Once per mount (the ref also absorbs Strict Mode's effect re-run):
    // later index changes are navigation, not opens.
    if (openedRef.current) return;
    openedRef.current = true;
    posthog.capture('clip_viewer_opened', {
      platform: 'web',
      source: deepLinked ? 'deep_link' : source,
      clips_count: clips.length,
      clip_index: index,
    });
  }, [deepLinked, source, clips.length, index]);

  // Fires when the viewer starts leaving, whatever closed it: our controls set
  // the method first; browser Back (popstate) closes from outside → 'back'.
  useEffect(() => {
    const session = sessionRef.current;
    if (isPresent || !session || session.closed) return;
    session.closed = true;
    posthog.capture('clip_viewer_closed', {
      platform: 'web',
      clips_viewed: session.viewed.size,
      duration_ms: Date.now() - session.openedAt,
      method: closeMethodRef.current ?? 'back',
    });
  }, [isPresent]);

  const onClose = useCallback(
    (method: CloseMethod) => {
      closeMethodRef.current = method;
      closeViewer();
    },
    [closeViewer],
  );

  const toggleMuted = () => {
    const next = !muted;
    setMuted(next);
    posthog.capture('clip_viewer_muted_toggled', { platform: 'web', muted: next });
  };

  const hasPrev = index > 0;
  const hasNext = index < clips.length - 1;

  const goPrev = useCallback(
    (method: NavMethod) => {
      if (index <= 0) return;
      setDirection(-1);
      onIndexChange(index - 1);
      posthog.capture('clip_viewer_navigated', { platform: 'web', direction: 'prev', method });
    },
    [index, onIndexChange],
  );

  const goNext = useCallback(
    (method: NavMethod) => {
      if (index >= clips.length - 1) return;
      setDirection(1);
      onIndexChange(index + 1);
      posthog.capture('clip_viewer_navigated', { platform: 'web', direction: 'next', method });
    },
    [index, clips.length, onIndexChange],
  );

  const closeComments = useCallback(() => {
    setCommentsOpen(false);
    commentsButtonRef.current?.focus();
  }, []);

  const nudgeSignIn = () => {
    setSignInNudge(true);
    window.setTimeout(() => setSignInNudge(false), 4000);
  };

  const onReact = () => {
    if (!viewerId) {
      nudgeSignIn();
      return;
    }
    const next = engagement.toggle(clip.id);
    if (next !== null) posthog.capture('clip_reaction_toggled', { platform: 'web', reacted: next });
  };

  const openComments = () => {
    if (commentsOpen) {
      closeComments();
      return;
    }
    setCommentsOpen(true);
    posthog.capture('clip_comments_opened', { platform: 'web', comments_count: counts.comment_count });
  };

  // Counted views: signed-in viewers, not the owner, once ~2s has played.
  // The server dedupes to one per viewer per clip per day.
  const { available: engagementAvailable, adjust: adjustStats } = engagement;
  const onViewQualified = useCallback(
    (clipId: string, ownerId: string) => {
      if (!engagementAvailable || !viewerId || viewerId === ownerId) return;
      void recordClipView(clipId).then((counted) => {
        if (counted) adjustStats(clipId, 'view_count', 1);
      });
    },
    [engagementAvailable, viewerId, adjustStats],
  );

  // Scroll lock + focus in/out.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // The share sheet handles its own keys.
      if (shareOpen) return;
      const target = e.target instanceof Element ? e.target : null;
      // Typing in a text field (the comment composer) never drives the viewer.
      if (e.key !== 'Tab' && target?.closest('textarea, input, [contenteditable="true"]')) return;
      if (e.key === 'Escape' && commentsOpen) {
        e.preventDefault();
        closeComments();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose('esc');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        goNext('key');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev('key');
      } else if (e.key === ' ' && !target?.closest('button, a, input, [role="slider"]')) {
        e.preventDefault();
        togglePlayRef.current?.();
      } else if (e.key === 'Tab' && dialogRef.current) {
        // Focus trap.
        const nodes = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === dialogRef.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev, onClose, commentsOpen, closeComments, shareOpen]);

  // One wheel/trackpad gesture moves one clip: trackpad inertia keeps firing
  // events for a while, so stay locked until the events stop.
  const onWheel = (e: React.WheelEvent) => {
    const now = performance.now();
    const w = wheelRef.current;
    const gap = now - w.last;
    w.last = now;
    if (now < w.lockUntil) {
      if (gap < WHEEL_GESTURE_GAP_MS) w.lockUntil = Math.max(w.lockUntil, now + WHEEL_GESTURE_GAP_MS);
      return;
    }
    if (gap > WHEEL_GESTURE_GAP_MS) w.acc = 0;
    w.acc += e.deltaY;
    if (Math.abs(w.acc) < WHEEL_STEP) return;
    const down = w.acc > 0;
    w.acc = 0;
    w.lockUntil = now + WHEEL_LOCK_MS;
    if (down) goNext('wheel');
    else goPrev('wheel');
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dy) < SWIPE_MIN_PX || Math.abs(dy) < Math.abs(dx)) return;
    swipedRef.current = true;
    window.setTimeout(() => (swipedRef.current = false), 400);
    if (dy < 0) goNext('swipe');
    else goPrev('swipe');
  };

  const shareUrl = `${typeof window === 'undefined' ? 'https://gamerplug.app' : window.location.origin}/profile/${encodeURIComponent(creator.gamertag)}?clip=${clip.id}`;

  const avatarUrl = getDisplayImageUrl(creator.profileImageUrl, 128);
  const initials = creator.gamertag.slice(0, 2).toUpperCase();

  const slideVariants: Variants = {
    enter: (dir: number) => (reduceMotion ? { opacity: 0 } : { y: dir > 0 ? '100%' : '-100%' }),
    center: { y: 0, opacity: 1 },
    exit: (dir: number) => (reduceMotion ? { opacity: 0 } : { y: dir > 0 ? '-100%' : '100%' }),
  };

  return (
    <motion.div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Clips by @${creator.gamertag}`}
      tabIndex={-1}
      className="fixed inset-0 z-[100] touch-none overscroll-contain bg-black text-white outline-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.2 }}
      onWheel={onWheel}
    >
      {/* Soft wash of the current clip behind the card (desktop). */}
      {clip.thumbnail_url && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={clip.thumbnail_url}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 hidden h-full w-full scale-110 object-cover opacity-25 blur-3xl md:block"
        />
      )}
      {/* Backdrop click closes. */}
      <div className="absolute inset-0" aria-hidden onClick={() => onClose('backdrop')} />

      <p className="sr-only" aria-live="polite">
        Clip {index + 1} of {clips.length}
      </p>

      <button
        type="button"
        onClick={() => onClose('x')}
        aria-label="Close"
        className="absolute left-3 top-[max(0.75rem,env(safe-area-inset-top))] z-30 flex size-11 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-white/20 md:left-6 md:top-6 md:bg-white/10"
      >
        <X size={20} />
      </button>

      <div
        className={`pointer-events-none relative z-10 flex h-full w-full items-center justify-center md:gap-5 md:py-6 ${commentsOpen ? 'md:pr-[400px]' : ''}`}
      >
        {/* Stage */}
        <div
          className={`pointer-events-auto relative h-full w-full overflow-hidden bg-black md:aspect-[9/16] md:h-auto md:rounded-2xl md:shadow-[0_30px_80px_rgba(0,0,0,0.6)] md:ring-1 md:ring-white/10 ${commentsOpen ? 'md:w-[min(calc((100dvh-3rem)*9/16),calc(100vw-15rem-400px))]' : 'md:w-[min(calc((100dvh-3rem)*9/16),calc(100vw-15rem))]'}`}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={clip.id}
              className="absolute inset-0"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: reduceMotion ? 0.15 : 0.38, ease: SLIDE_EASE }}
            >
              <ClipSlide
                clip={clip}
                gamertag={creator.gamertag}
                muted={muted}
                onAutoplayBlocked={onAutoplayBlocked}
                togglePlayRef={togglePlayRef}
                swipedRef={swipedRef}
                onViewQualified={onViewQualified}
              />
            </motion.div>
          </AnimatePresence>

          <button
            type="button"
            onClick={toggleMuted}
            aria-label={muted ? 'Unmute' : 'Mute'}
            aria-pressed={!muted}
            className="absolute right-3 top-[max(0.75rem,env(safe-area-inset-top))] z-20 flex size-11 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 md:left-3 md:right-auto md:top-3"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        {/* Action rail: overlays the video on mobile, sits beside it on desktop. */}
        <div className="pointer-events-auto absolute bottom-28 right-2 z-20 flex flex-col items-center gap-5 md:static md:self-end md:pb-4">
          <button
            type="button"
            onClick={() => onClose('back')}
            aria-label={`Back to @${creator.gamertag}'s profile`}
            className="flex size-12 cursor-pointer items-center justify-center rounded-full ring-2 ring-white/20 transition-shadow hover:ring-white/50"
          >
            <Avatar className="size-12 bg-neutral-900">
              {avatarUrl && <AvatarImage src={avatarUrl} alt="" className="object-cover" />}
              <AvatarFallback className="bg-neutral-800 text-xs font-semibold text-white">{initials}</AvatarFallback>
            </Avatar>
          </button>

          {engagement.available && (
            <>
              <RailButton
                label={formatCount(counts.reaction_count)}
                ariaLabel={reacted ? `Unlike clip (${counts.reaction_count} likes)` : `Like clip (${counts.reaction_count} likes)`}
                pressed={reacted}
                onClick={onReact}
              >
                <motion.span
                  key={reacted ? 'on' : 'off'}
                  className="flex"
                  initial={reduceMotion || !reacted ? false : { scale: 0.6 }}
                  animate={{ scale: reduceMotion || !reacted ? 1 : [0.6, 1.25, 1] }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                  <Heart
                    size={22}
                    className={reacted ? 'text-primary' : 'text-white'}
                    fill={reacted ? 'currentColor' : 'none'}
                  />
                </motion.span>
              </RailButton>

              <RailButton
                ref={commentsButtonRef}
                label={formatCount(counts.comment_count)}
                ariaLabel={`Comments (${counts.comment_count})`}
                expanded={commentsOpen}
                onClick={openComments}
              >
                <MessageCircle size={21} />
              </RailButton>

              <div
                className="flex flex-col items-center gap-1.5"
                role="img"
                aria-label={`${counts.view_count} ${counts.view_count === 1 ? 'view' : 'views'}`}
              >
                <span className="flex size-12 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm md:bg-white/10" aria-hidden>
                  <Eye size={20} />
                </span>
                <span className="text-xs font-semibold tabular-nums text-white/90 drop-shadow md:text-white/70" aria-hidden>
                  {formatCount(counts.view_count)}
                </span>
              </div>
            </>
          )}

          <RailButton label="Share" ariaLabel="Share clip" onClick={() => setShareOpen(true)}>
            <Share2 size={20} />
          </RailButton>
          <ShareSheet
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            url={shareUrl}
            text={`@${creator.gamertag} on GamerPlug`}
            onShare={(method) => posthog.capture('clip_viewer_shared', { platform: 'web', method })}
          />
        </div>
      </div>

      {/* Comments: right panel on desktop, bottom sheet on mobile. */}
      <AnimatePresence>
        {commentsOpen && engagement.available && (
          <ClipComments
            key="clip-comments"
            clipId={clip.id}
            clipOwnerId={clip.user_id}
            viewerId={viewerId}
            count={counts.comment_count}
            onCountChange={(delta) => engagement.adjust(clip.id, 'comment_count', delta)}
            onClose={closeComments}
            isDesktop={isDesktop}
            reduceMotion={reduceMotion}
          />
        )}
      </AnimatePresence>

      {/* Logged-out heart tap. */}
      <AnimatePresence>
        {signInNudge && (
          <motion.div
            className="absolute inset-x-4 bottom-[max(1.5rem,env(safe-area-inset-bottom))] z-50 mx-auto max-w-sm rounded-2xl border border-white/10 bg-neutral-950/95 p-3 backdrop-blur-sm"
            role="status"
            initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <SignInPrompt text="Sign in to like clips" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Previous / next (desktop). */}
      <div
        className={`absolute top-1/2 z-20 hidden -translate-y-1/2 flex-col gap-4 md:flex ${commentsOpen ? 'right-[424px]' : 'right-6'}`}
      >
        <NavButton label="Previous clip" disabled={!hasPrev} onClick={() => goPrev('button')}>
          <ChevronUp size={22} />
        </NavButton>
        <NavButton label="Next clip" disabled={!hasNext} onClick={() => goNext('button')}>
          <ChevronDown size={22} />
        </NavButton>
      </div>
    </motion.div>
  );
}

function RailButton({
  ref,
  label,
  ariaLabel,
  pressed,
  expanded,
  onClick,
  children,
}: {
  ref?: React.Ref<HTMLButtonElement>;
  label: string;
  ariaLabel: string;
  pressed?: boolean;
  expanded?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={pressed}
      aria-expanded={expanded}
      className="group flex cursor-pointer flex-col items-center gap-1.5"
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm transition-colors group-hover:bg-white/20 md:bg-white/10">
        {children}
      </span>
      <span className="text-xs font-semibold tabular-nums text-white/90 drop-shadow md:text-white/70" aria-hidden>
        {label}
      </span>
    </button>
  );
}

function NavButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex size-12 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 disabled:cursor-default disabled:opacity-30 disabled:hover:bg-white/10"
    >
      {children}
    </button>
  );
}

function formatClipDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function ClipSlide({
  clip,
  gamertag,
  muted,
  onAutoplayBlocked,
  togglePlayRef,
  swipedRef,
  onViewQualified,
}: {
  clip: Clip;
  gamertag: string;
  muted: boolean;
  onAutoplayBlocked: () => void;
  togglePlayRef: MutableRefObject<(() => void) | null>;
  swipedRef: MutableRefObject<boolean>;
  onViewQualified: (clipId: string, ownerId: string) => void;
}) {
  const isPresent = useIsPresent();
  const videoRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<ReturnType<typeof startClipView> | null>(null);
  // Seconds actually played on this slide (seeks don't count), for the
  // counted-view threshold.
  const playedRef = useRef({ last: 0, total: 0, recorded: false });
  const [paused, setPaused] = useState(false);

  useLayoutEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  const play = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {
      // Sound autoplay can be refused (no user gesture yet): retry muted.
      if (!v.muted) {
        v.muted = true;
        onAutoplayBlocked();
        v.play().catch(() => setPaused(true));
      } else {
        setPaused(true);
      }
    });
  }, [onAutoplayBlocked]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) play();
    else v.pause();
  }, [play]);

  // Only the slide on screen plays, tracks a view, and owns the Space key.
  useEffect(() => {
    if (!isPresent) {
      videoRef.current?.pause();
      return;
    }
    play();
    togglePlayRef.current = togglePlay;
    const session = startClipView({ clipId: clip.id, surface: 'video_modal', durationSec: clip.duration });
    viewRef.current = session;
    return () => {
      session.end();
      viewRef.current = null;
      if (togglePlayRef.current === togglePlay) togglePlayRef.current = null;
    };
  }, [isPresent, clip.id, clip.duration, play, togglePlay, togglePlayRef]);

  // Progress bar follows playback every frame (timeupdate is too coarse).
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const v = videoRef.current;
      if (v && barRef.current && v.duration > 0) {
        barRef.current.style.transform = `scaleX(${Math.min(1, v.currentTime / v.duration)})`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const seekTo = (clientX: number) => {
    const v = videoRef.current;
    const el = sliderRef.current;
    if (!v || !el || !(v.duration > 0)) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    v.currentTime = ratio * v.duration;
  };

  const caption = clipCaption(clip);
  const date = formatClipDate(clip.created_at);

  return (
    <div className="relative h-full w-full">
      <video
        ref={videoRef}
        className="h-full w-full cursor-pointer object-contain"
        src={clip.video_url}
        poster={clip.thumbnail_url || undefined}
        playsInline
        muted
        preload="auto"
        controlsList="nodownload"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onClick={() => {
          if (swipedRef.current) return;
          togglePlay();
        }}
        onPlay={() => setPaused(false)}
        onPause={() => setPaused(true)}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          viewRef.current?.tick(v.currentTime, v.duration);
          const p = playedRef.current;
          const step = v.currentTime - p.last;
          p.last = v.currentTime;
          if (step > 0 && step < 1) p.total += step;
          if (!p.recorded && isPresent && p.total >= VIEW_RECORD_AFTER_SEC) {
            p.recorded = true;
            onViewQualified(clip.id, clip.user_id);
          }
          if (sliderRef.current && v.duration > 0) {
            sliderRef.current.setAttribute('aria-valuenow', String(Math.round(v.currentTime)));
            sliderRef.current.setAttribute('aria-valuemax', String(Math.round(v.duration)));
          }
        }}
        onEnded={(e) => {
          // Loop like TikTok, but let the tracker see the completion first.
          viewRef.current?.markCompleted();
          const v = e.currentTarget;
          v.currentTime = 0;
          if (isPresent) play();
        }}
      />

      {paused && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex size-16 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm">
            <Play size={28} fill="white" className="ml-1 text-white" />
          </span>
        </div>
      )}

      {/* Creator + caption */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent px-4 pb-6 pr-20 pt-16 md:pr-4"
        style={{ paddingBottom: 'max(1.5rem, calc(env(safe-area-inset-bottom) + 1rem))' }}
      >
        <p className="flex items-baseline gap-2 text-sm">
          <span className="font-space-mono font-bold text-white">@{gamertag}</span>
          {date && <span className="text-xs tabular-nums text-white/60">{date}</span>}
        </p>
        {caption && <ExpandableCaption text={caption} />}
      </div>

      {/* Progress (seekable) */}
      <div
        ref={sliderRef}
        role="slider"
        tabIndex={0}
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={clip.duration ?? 0}
        aria-valuenow={0}
        className="group absolute inset-x-0 bottom-0 z-10 flex h-4 cursor-pointer items-end outline-none"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          seekTo(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) seekTo(e.clientX);
        }}
        onKeyDown={(e) => {
          const v = videoRef.current;
          if (!v || !(v.duration > 0)) return;
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            v.currentTime = Math.min(v.duration, v.currentTime + 5);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            v.currentTime = Math.max(0, v.currentTime - 5);
          }
        }}
      >
        <div className="relative h-0.5 w-full bg-white/20 transition-[height] group-hover:h-1 group-focus-visible:h-1">
          <div ref={barRef} className="absolute inset-0 origin-left scale-x-0 bg-primary" />
        </div>
      </div>
    </div>
  );
}

function ExpandableCaption({ text }: { text: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <div className="pointer-events-auto mt-1 text-sm text-white/90">
      <p ref={ref} className={expanded ? 'whitespace-pre-line break-words' : 'line-clamp-2 break-words'}>
        {text}
      </p>
      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          aria-expanded={expanded}
          className="mt-0.5 min-h-11 cursor-pointer text-sm font-semibold text-white/70 transition-colors hover:text-white"
        >
          {expanded ? 'less' : 'more'}
        </button>
      )}
    </div>
  );
}
